/**
 * Organisational readiness (gap assessment) — the project tab (Step 5).
 *
 * - Opening the tab only reads. A writer (owner/editor with a writer role; administrators and super
 *   users on every project) presses "Start readiness review" to create it (C-090, C-099).
 * - Automation only SUGGESTS a level (capped at 3), shown beside the item with its evidence. Nothing
 *   counts until the assessor sets or confirms a level; "Re-run automation" refreshes suggestions
 *   only (C-088, C-092).
 * - One scale: 1 Not started · 2 Started · 3 Largely in place · 4 Fully in place; averages of the
 *   answered items as "x.x of 4" with "n of 25 answered"; no percentages or tiers (C-094, C-095).
 * - Editing a note never touches the level; only changed fields are sent (C-091, C-103).
 * - Any change clears Verified; "Verify & take snapshot" is always offered to writers (C-098).
 * - One renderer: "View report" opens the server page for every member (C-101).
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

type GapLevel = 'L1' | 'L2' | 'L3' | 'L4';
const LEVELS: GapLevel[] = ['L1', 'L2', 'L3', 'L4'];
const LEVEL_LABEL: Record<GapLevel, string> = { L1: 'Not started', L2: 'Started', L3: 'Largely in place', L4: 'Fully in place' };
const levelText = (l: GapLevel) => `${l.slice(1)} ${LEVEL_LABEL[l]}`;
const of4 = (x: number | null | undefined) => (x == null ? '—' : `${x.toFixed(1)} of 4`);

interface AnswerChoice { current_state: string; gap_analysis: string; recommended_action: string }
interface GapQuestion {
  question_id: string; category: string; assessment_area: string; target_state_summary: string;
  answer_choices: Record<GapLevel, AnswerChoice>; current_state_source: 'Automated' | 'User input' | 'Default value'; display_order: number;
}
interface GapItem {
  question_id: string; answer: GapLevel | null; free_text: string | null;
  current_state_source: 'User input' | 'Not answered'; suggestion: { level: GapLevel; evidence: string } | null;
}
interface Summary {
  answered: number; total: number; overall_avg: number | null;
  categories: Array<{ category: string; avg: number | null; answered: number; total: number; weakest: { area: string; level: number } | null }>;
  not_started: Array<{ question_id: string; area: string; category: string }>;
}
interface ReadinessData {
  project_id: string; started: boolean; can_edit: boolean; questions: GapQuestion[];
  gap_analysis?: GapItem[]; summary?: Summary; is_verified_by_user?: 'Yes' | 'No';
  latest_snapshot?: { id: string; label: string; created_at: string } | null; updated_at?: string;
}
interface Snapshot { id: string; snapshot_label: string; created_at: string; answered: number; total: number; overall_avg: number | null }
/** Only the fields the assessor changed; absent = unchanged. */
interface Edit { answer?: GapLevel | null; free_text?: string }

export function GapAssessmentPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const key = ['gap-assessment', projectId];
  const [open, setOpen] = useState<string | null>(null);
  const [edits, setEdits] = useState<Map<string, Edit>>(new Map());
  const [showHistory, setShowHistory] = useState(false);

  const q = useQuery<ReadinessData>({ queryKey: key, queryFn: () => api.get<ReadinessData>(`/projects/${projectId}/gap-assessment`), enabled: !!projectId });
  const snapshotsQ = useQuery<Snapshot[]>({
    queryKey: ['gap-snapshots', projectId], enabled: showHistory,
    queryFn: () => api.get<Snapshot[]>(`/projects/${projectId}/gap-assessment/snapshots`),
  });
  const put = (d: ReadinessData) => qc.setQueryData<ReadinessData>(key, d);

  const start = useMutation({ mutationFn: () => api.post<ReadinessData>(`/projects/${projectId}/gap-assessment/start`), onSuccess: put });
  const rerun = useMutation({ mutationFn: () => api.post<ReadinessData>(`/projects/${projectId}/gap-assessment/run`), onSuccess: put });
  const save = useMutation({
    mutationFn: (answers: Array<{ question_id: string } & Edit>) => api.patch<ReadinessData>(`/projects/${projectId}/gap-assessment/answers`, { answers }),
    onSuccess: (d, sent) => {
      put(d);
      setEdits((prev) => { const n = new Map(prev); sent.forEach((a) => n.delete(a.question_id)); return n; });
    },
  });
  const verify = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/gap-assessment/verify`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ['gap-snapshots', projectId] }); },
  });

  const edit = (qid: string, patch: Edit) => setEdits((prev) => new Map(prev).set(qid, { ...prev.get(qid), ...patch }));

  if (q.isLoading) return <div className="flex items-center justify-center h-64 text-sm text-ink-500">Loading organisational readiness…</div>;
  if (q.error || !q.data) return <div className="p-8 text-sm text-accent">Failed to load organisational readiness. Please refresh.</div>;
  const data = q.data;
  const categories = [...new Set(data.questions.map((x) => x.category))];
  const scaleLine = <p className="text-sm text-ink-500 mt-1">25 items in 6 categories, each rated <span className="text-ink-700">1 Not started · 2 Started · 3 Largely in place · 4 Fully in place</span>. Automation only suggests a level from the project's data (at most 3); nothing counts until the assessor sets or confirms it.</p>;

  if (!data.started) {
    return (
      <div className="h-full overflow-y-auto"><div className="max-w-7xl mx-auto px-6 py-6 space-y-5" data-testid="readiness-not-started">
        <div><h2 className="font-display text-xl text-ink-900">Organisational readiness (gap assessment)</h2>{scaleLine}</div>
        <div className="border border-ink-200 rounded-md bg-white p-5">
          <p className="text-sm text-ink-800 font-medium">Organisational readiness: not yet assessed.</p>
          <p className="text-xs text-ink-500 mt-1">Reports show readiness only once the review has been answered and verified.</p>
          {data.can_edit ? (
            <button type="button" className="btn-primary text-xs mt-4" disabled={start.isPending} onClick={() => start.mutate()} data-testid="readiness-start">
              {start.isPending ? 'Starting…' : 'Start readiness review'}
            </button>
          ) : <p className="text-xs text-ink-500 mt-3">A project owner or editor starts the review.</p>}
          {start.error && <p className="text-xs text-accent mt-2">{(start.error as Error).message}</p>}
        </div>
        <ul className="grid grid-cols-2 gap-2 text-sm text-ink-700">
          {categories.map((c, i) => <li key={c} className="border border-ink-100 rounded-sm px-3 py-2"><span className="text-ink-400 font-mono text-xs mr-2">{i + 1}.</span>{c} <span className="text-xs text-ink-400">· {data.questions.filter((x) => x.category === c).length} items</span></li>)}
        </ul>
      </div></div>
    );
  }

  const items = new Map((data.gap_analysis ?? []).map((i) => [i.question_id, i]));
  const sm = data.summary!;
  const verified = data.is_verified_by_user === 'Yes';
  const dirty = edits.size;
  const effAnswer = (qid: string): GapLevel | null => { const e = edits.get(qid); return e && 'answer' in e ? e.answer ?? null : items.get(qid)?.answer ?? null; };
  const effNote = (qid: string): string => { const e = edits.get(qid); return e && e.free_text !== undefined ? e.free_text : items.get(qid)?.free_text ?? ''; };
  const saveCategory = (qs: GapQuestion[]) => {
    const payload = qs.filter((x) => edits.has(x.question_id)).map((x) => ({ question_id: x.question_id, ...edits.get(x.question_id)! }));
    if (payload.length) save.mutate(payload);
  };
  const suggestionsWaiting = (data.gap_analysis ?? []).filter((i) => i.suggestion && !i.answer).length;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl"><h2 className="font-display text-xl text-ink-900">Organisational readiness (gap assessment)</h2>{scaleLine}</div>
          <div className="rounded-md border border-ink-200 bg-white p-4 min-w-[220px]" data-testid="readiness-overall">
            <div className="text-xs text-ink-500 uppercase tracking-wide mb-1">Overall</div>
            <div className="text-3xl font-display font-bold text-ink-900">{of4(sm.overall_avg)}</div>
            <div className="text-sm text-ink-600 mt-0.5">{sm.answered} of {sm.total} answered</div>
            <div className={`text-xs mt-2 font-medium ${verified ? 'text-green-700' : 'text-amber-700'}`} data-testid="readiness-status">
              {verified ? `✓ ${data.latest_snapshot?.label ?? 'Verified'}` : 'Not verified: reports show readiness only when verified'}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-ink-100">
          {data.can_edit && (<>
            <button type="button" className="btn-secondary text-xs" disabled={rerun.isPending} onClick={() => rerun.mutate()} title="Refreshes the suggestions only; answers, notes and Verified are kept">
              {rerun.isPending ? 'Running…' : '↻ Re-run automation (suggestions only)'}
            </button>
            <button type="button" className="btn-primary text-xs" data-testid="readiness-verify"
              disabled={verify.isPending || sm.answered === 0 || dirty > 0}
              title={dirty > 0 ? 'Save your changes first' : sm.answered === 0 ? 'Answer at least one item first' : ''}
              onClick={() => { if (sm.answered < sm.total && !window.confirm(`${sm.total - sm.answered} of ${sm.total} items are not answered. Verify and take a snapshot anyway?`)) return; verify.mutate(); }}>
              {verify.isPending ? 'Verifying…' : verified ? '✓ Verify again & take snapshot' : '✓ Verify & take snapshot'}
            </button>
          </>)}
          <button type="button" className="btn-secondary text-xs" onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'Hide history' : 'History'}</button>
          {!data.can_edit && <span className="text-xs text-ink-500" data-testid="readiness-readonly">Read only: project owners and editors answer the review.</span>}
          {data.updated_at && <span className="text-xs text-ink-400 ml-auto">Updated {new Date(data.updated_at).toLocaleString()}</span>}
        </div>

        {suggestionsWaiting > 0 && (
          <div className="text-xs text-ink-700 bg-ink-50 border border-ink-200 rounded-sm px-3 py-2">
            {suggestionsWaiting} item{suggestionsWaiting > 1 ? 's have' : ' has'} a suggested level waiting for the assessor. Suggestions are not answers until set or confirmed.
          </div>
        )}
        {[rerun.error, save.error, verify.error].filter(Boolean).map((e, i) => <div key={i} className="text-xs text-accent bg-accent/5 border border-accent/20 rounded-sm px-3 py-2">{(e as Error).message}</div>)}
        {data.questions.length === 0 && <div className="py-12 text-center text-ink-500 text-sm border border-ink-100 rounded-md">No items loaded. The question bank is seeded by migration V037.</div>}

        {showHistory && (
          <div className="border border-ink-200 rounded-md bg-white p-4" data-testid="readiness-history">
            <h3 className="text-sm font-medium text-ink-900 mb-2">Verified snapshots</h3>
            {snapshotsQ.isLoading ? <p className="text-xs text-ink-500">Loading…</p> : (snapshotsQ.data ?? []).length === 0 ? <p className="text-xs text-ink-500">None yet.</p> : (
              <table className="w-full text-sm"><thead><tr className="text-[11px] text-ink-500 uppercase"><th className="text-left py-1">Snapshot</th><th className="text-right">Answered</th><th className="text-right">Overall</th></tr></thead>
                <tbody>{snapshotsQ.data!.map((s) => <tr key={s.id} className="border-t border-ink-100"><td className="py-1.5">{s.snapshot_label}</td><td className="text-right font-mono text-xs">{s.answered} of {s.total}</td><td className="text-right font-mono text-xs">{of4(s.overall_avg)}</td></tr>)}</tbody></table>
            )}
          </div>
        )}

        <div className="space-y-3">
          {categories.map((category, ci) => {
            const qs = data.questions.filter((x) => x.category === category);
            const cs = sm.categories.find((c) => c.category === category);
            const isOpen = open === category;
            const catDirty = qs.filter((x) => edits.has(x.question_id)).length;
            return (
              <section key={category} className="border border-ink-200 rounded-md overflow-hidden bg-white">
                <button type="button" onClick={() => setOpen(isOpen ? null : category)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-ink-50/50" data-testid={`readiness-cat-${ci + 1}`}>
                  <div className="flex items-center gap-2 text-left">
                    <span className="text-xs font-mono text-ink-400 w-5">{ci + 1}.</span>
                    <span className="font-medium text-sm text-ink-900">{category}</span>
                    <span className="text-[11px] text-ink-600 bg-ink-50 border border-ink-200 rounded-full px-2 py-0.5">{of4(cs?.avg)} · {cs?.answered ?? 0} of {cs?.total ?? qs.length} answered</span>
                    {catDirty > 0 && <span className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-medium">{catDirty} unsaved</span>}
                  </div>
                  <span className="text-ink-400 text-sm">{isOpen ? '▲' : '▼'}</span>
                </button>
                {isOpen && (
                  <div className="border-t border-ink-100 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-ink-50 border-b border-ink-100 text-[10px] font-medium text-ink-500 uppercase tracking-wider">
                        <th className="text-left px-3 py-2 w-[24%]">Item / target</th><th className="text-left px-3 py-2 w-[17%]">Level (assessor)</th>
                        <th className="text-left px-3 py-2 w-[22%]">Automation suggests</th><th className="text-left px-3 py-2 w-[19%]">What this level means</th><th className="text-left px-3 py-2">Note</th>
                      </tr></thead>
                      <tbody>
                        {qs.map((x) => {
                          const it = items.get(x.question_id);
                          const a = effAnswer(x.question_id);
                          const sg = it?.suggestion ?? null;
                          const rowDirty = edits.has(x.question_id);
                          return (
                            <tr key={x.question_id} className={`border-b border-ink-100 align-top ${rowDirty ? 'bg-amber-50/40' : ''}`} data-testid={`readiness-item-${x.question_id}`}>
                              <td className="px-3 py-2"><div className="font-medium text-ink-900">{x.assessment_area}</div><div className="text-xs text-ink-500">{x.target_state_summary}</div></td>
                              <td className="px-3 py-2">
                                <select aria-label={`Level for ${x.assessment_area}`} disabled={!data.can_edit} value={a ?? ''} className="text-xs border border-ink-200 rounded-sm px-2 py-1 w-full bg-white"
                                  onChange={(e) => edit(x.question_id, { answer: (e.target.value || null) as GapLevel | null })}>
                                  <option value="">— Not answered —</option>
                                  {LEVELS.map((l) => <option key={l} value={l}>{levelText(l)}</option>)}
                                </select>
                                {!a && <div className="text-[11px] text-amber-700 mt-1">Not answered</div>}
                              </td>
                              <td className="px-3 py-2 text-xs">
                                {sg ? (<>
                                  <div className="font-medium text-ink-800">{levelText(sg.level)}</div>
                                  <div className="text-ink-500">{sg.evidence}</div>
                                  {data.can_edit && a !== sg.level && <button type="button" className="text-accent underline mt-1" onClick={() => edit(x.question_id, { answer: sg.level })}>Use suggestion</button>}
                                </>) : <span className="text-ink-400">{x.current_state_source === 'Automated' ? 'No suggestion yet' : 'Assessor only'}</span>}
                              </td>
                              <td className="px-3 py-2 text-xs text-ink-600">{a ? x.answer_choices?.[a]?.current_state : '—'}</td>
                              <td className="px-3 py-2">
                                <textarea aria-label={`Note for ${x.assessment_area}`} disabled={!data.can_edit} rows={2} value={effNote(x.question_id)}
                                  onChange={(e) => edit(x.question_id, { free_text: e.target.value })}
                                  className="text-xs border border-ink-200 rounded-sm px-2 py-1 w-full resize-y" placeholder={data.can_edit ? 'Evidence or reason' : ''} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {data.can_edit && catDirty > 0 && (
                      <div className="flex justify-end gap-2 px-4 py-3 border-t border-ink-100 bg-amber-50/30">
                        <button type="button" className="btn-secondary text-xs" onClick={() => setEdits((p) => { const n = new Map(p); qs.forEach((x) => n.delete(x.question_id)); return n; })}>Discard</button>
                        <button type="button" className="btn-primary text-xs" disabled={save.isPending} onClick={() => saveCategory(qs)} data-testid={`readiness-save-${ci + 1}`}>
                          {save.isPending ? 'Saving…' : `Save ${catDirty} change${catDirty > 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
